import React, { useState, useEffect, useCallback } from 'react';
import API from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { 
  Button, 
  Card, 
  Form, 
  Input, 
  Select, 
  Space, 
  Table, 
  Typography, 
  Modal, 
  Row, 
  Col, 
  Statistic,
  Tag,
  message,
  Tooltip,
  Tabs,
  Divider,
  Popconfirm,
  Badge
} from 'antd';
import { 
  BugOutlined, 
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  BarChartOutlined,
  UserOutlined,
  SearchOutlined,
  ReloadOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const AdminBugReportManagement = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  
  // 筛选条件
  const [filters, setFilters] = useState({
    status: undefined,
    bugType: undefined,
    priority: undefined,
    assignedTo: undefined,
    submitter: undefined,
    search: undefined
  });
  
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();

  // BUG类型配置
  const bugTypes = [
    { value: '功能异常', label: '功能异常', color: 'red' },
    { value: '界面问题', label: '界面问题', color: 'orange' },
    { value: '性能问题', label: '性能问题', color: 'blue' },
    { value: '数据错误', label: '数据错误', color: 'purple' },
    { value: '安全问题', label: '安全问题', color: 'volcano' },
    { value: '其他', label: '其他', color: 'default' }
  ];

  // 优先级配置
  const priorities = [
    { value: '低', label: '低', color: 'default' },
    { value: '中', label: '中', color: 'processing' },
    { value: '高', label: '高', color: 'warning' },
    { value: '紧急', label: '紧急', color: 'error' }
  ];

  // 状态配置
  const statuses = [
    { value: '待处理', label: '待处理', color: 'default', icon: <ClockCircleOutlined /> },
    { value: '处理中', label: '处理中', color: 'processing', icon: <ExclamationCircleOutlined /> },
    { value: '已修复', label: '已修复', color: 'success', icon: <CheckCircleOutlined /> },
    { value: '无法复现', label: '无法复现', color: 'warning', icon: <ExclamationCircleOutlined /> },
    { value: '不是BUG', label: '不是BUG', color: 'error', icon: <ExclamationCircleOutlined /> },
    { value: '已关闭', label: '已关闭', color: 'default', icon: <ExclamationCircleOutlined /> }
  ];

  // 获取所有反馈列表
  const fetchReports = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...filters
      };
      
      // 过滤掉undefined值
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await API.get('/api/admin/bug-report/all-reports', { params });
      
      setReports(response.data.reports);
      setTotalReports(response.data.totalReports);
      setCurrentPage(response.data.currentPage);
    } catch (error) {
      message.error('获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // 获取统计信息
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/bug-report/statistics/overview');
      setStatistics(response.data);
    } catch (error) {
      message.error('获取统计信息失败');
    }
  }, []);

  // 获取可分配用户列表
  const fetchAssignableUsers = useCallback(async () => {
    try {
      const response = await API.get('/api/admin/bug-report/assignable-users');
      setAssignableUsers(response.data.users);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  }, []);

  // 获取反馈详情
  const fetchReportDetail = async (reportId) => {
    try {
      setLoading(true);
      const response = await API.get(`/api/admin/bug-report/reports/${reportId}`);
      setSelectedReport(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      message.error('获取反馈详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 编辑反馈
  const handleEdit = (record) => {
    setSelectedReport(record);
    form.setFieldsValue({
      status: record.status,
      priority: record.priority,
      assignedTo: record.assignedTo?.id || null,
      processingNotes: record.processingNotes || '',
      resolutionNotes: record.resolutionNotes || ''
    });
    setEditModalVisible(true);
  };

  // 更新反馈
  const handleUpdate = async (values) => {
    try {
      setLoading(true);
      await API.put(`/api/admin/bug-report/reports/${selectedReport.id}`, values);
      
      message.success('反馈更新成功');
      setEditModalVisible(false);
      form.resetFields();
      await fetchReports(currentPage);
      
      if (activeTab === 'statistics') {
        await fetchStatistics();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '更新反馈失败';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 删除反馈
  const handleDelete = async (reportId) => {
    try {
      await API.delete(`/api/admin/bug-report/reports/${reportId}`);
      message.success('反馈删除成功');
      await fetchReports(currentPage);
      
      if (activeTab === 'statistics') {
        await fetchStatistics();
      }
    } catch (error) {
      message.error('删除反馈失败');
    }
  };

  // 批量操作
  const handleBatchUpdate = async (values) => {
    try {
      setLoading(true);
      await API.put('/api/admin/bug-report/batch-update', {
        reportIds: selectedRowKeys,
        ...values
      });
      
      message.success(`成功更新 ${selectedRowKeys.length} 个反馈`);
      setBatchModalVisible(false);
      batchForm.resetFields();
      setSelectedRowKeys([]);
      await fetchReports(currentPage);
      
      if (activeTab === 'statistics') {
        await fetchStatistics();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || '批量更新失败';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 处理分页变化
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchReports(page);
  };

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // 重置筛选
  const handleResetFilters = () => {
    setFilters({
      status: undefined,
      bugType: undefined,
      priority: undefined,
      assignedTo: undefined,
      submitter: undefined,
      search: undefined
    });
    setCurrentPage(1);
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('zh-CN');
  };

  // 获取配置
  const getStatusConfig = (status) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const getTypeConfig = (type) => {
    return bugTypes.find(t => t.value === type) || bugTypes[0];
  };

  const getPriorityConfig = (priority) => {
    return priorities.find(p => p.value === priority) || priorities[1];
  };

  // 安全地获取用户显示名称
  const getDisplayName = (user) => {
    if (!user) return '';
    if (typeof user === 'string') return user;
    if (typeof user.displayName === 'string') return user.displayName;
    if (typeof user.displayName === 'object' && user.displayName && user.displayName.displayName) return user.displayName.displayName;
    return user.username || user.email || 'Unknown';
  };

  // 表格列定义
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>
          {text.slice(-8)}
        </Text>
      )
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Tooltip title={text}>
            <Text strong style={{ cursor: 'pointer' }} onClick={() => fetchReportDetail(record.id)}>
              {text}
            </Text>
          </Tooltip>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            提交者: {record.submitter.username}
          </Text>
        </div>
      )
    },
    {
      title: '类型',
      dataIndex: 'bugType',
      key: 'bugType',
      width: 100,
      render: (type) => {
        const config = getTypeConfig(type);
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority) => {
        const config = getPriorityConfig(priority);
        return <Tag color={config.color}>{config.label}</Tag>;
      }
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const config = getStatusConfig(status);
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      }
    },
    {
      title: '处理人',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      width: 100,
      render: (assignedTo) => (
        assignedTo ? (
          <Tag icon={<UserOutlined />} color="blue">
            {getDisplayName(assignedTo)}
          </Tag>
        ) : (
          <Text type="secondary">未分配</Text>
        )
      )
    },
    {
      title: '提交时间',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 150,
      render: (date) => formatDate(date)
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => fetchReportDetail(record.id)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Popconfirm
              title="确定删除这个反馈吗？"
              onConfirm={() => handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  // 初始化数据
  useEffect(() => {
    if (activeTab === 'list') {
      fetchReports();
      fetchAssignableUsers();
      fetchStatistics(); // 为了显示小红点，需要加载统计数据
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab, fetchReports, fetchStatistics, fetchAssignableUsers]);



  // 渲染反馈列表
  const renderReportList = () => (
    <div>
      {/* 筛选器 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={3}>
            <Text strong>筛选条件：</Text>
          </Col>
          <Col span={3}>
            <Select
              placeholder="状态"
              allowClear
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
            >
              {statuses.map(status => (
                <Option key={status.value} value={status.value}>
                  <Tag icon={status.icon} color={status.color}>{status.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="类型"
              allowClear
              style={{ width: '100%' }}
              value={filters.bugType}
              onChange={(value) => handleFilterChange('bugType', value)}
            >
              {bugTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  <Tag color={type.color}>{type.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="优先级"
              allowClear
              style={{ width: '100%' }}
              value={filters.priority}
              onChange={(value) => handleFilterChange('priority', value)}
            >
              {priorities.map(priority => (
                <Option key={priority.value} value={priority.value}>
                  <Tag color={priority.color}>{priority.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="处理人"
              allowClear
              style={{ width: '100%' }}
              value={filters.assignedTo}
              onChange={(value) => handleFilterChange('assignedTo', value)}
            >
              <Option value="unassigned">未分配</Option>
              {assignableUsers.map(user => (
                <Option key={user._id} value={user._id}>
                  {getDisplayName(user)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Input
              placeholder="搜索标题/描述/提交者"
              allowClear
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onPressEnter={() => fetchReports(1)}
              suffix={<SearchOutlined />}
            />
          </Col>
          <Col span={4}>
            <Space>
              <Button onClick={() => fetchReports(1)} icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleResetFilters}>
                重置
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* 操作栏 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text strong>已选择 {selectedRowKeys.length} 项</Text>
              {selectedRowKeys.length > 0 && (
                <Button
                  type="primary"
                  icon={<SettingOutlined />}
                  onClick={() => setBatchModalVisible(true)}
                >
                  批量操作
                </Button>
              )}
            </Space>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchReports(currentPage)}
            >
              刷新
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 反馈列表 */}
      <Card title="BUG反馈列表">
        <Table
          columns={columns}
          dataSource={reports}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading}
          pagination={{
            current: currentPage,
            total: totalReports,
            pageSize: 20,
            showTotal: (total, range) => 
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            showSizeChanger: false,
            onChange: handlePageChange
          }}
          size="small"
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );

  // 渲染统计信息
  const renderStatistics = () => (
    <div>
      {/* 概览统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总反馈数"
              value={statistics.overview?.totalReports || 0}
              prefix={<BugOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="待处理"
              value={statistics.overview?.pendingReports || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="处理中"
              value={statistics.overview?.processingReports || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已修复"
              value={statistics.overview?.resolvedReports || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 状态分布 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Card title="状态分布">
            <Row gutter={16}>
              {Object.entries(statistics.statusStatistics || {}).map(([status, count]) => {
                const config = getStatusConfig(status);
                return (
                  <Col span={8} key={status} style={{ marginBottom: 16 }}>
                    <Statistic
                      title={<Tag icon={config.icon} color={config.color}>{status}</Tag>}
                      value={count}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="优先级分布">
            <Row gutter={16}>
              {Object.entries(statistics.priorityStatistics || {}).map(([priority, count]) => {
                const config = getPriorityConfig(priority);
                return (
                  <Col span={6} key={priority} style={{ marginBottom: 16 }}>
                    <Statistic
                      title={<Tag color={config.color}>{priority}</Tag>}
                      value={count}
                    />
                  </Col>
                );
              })}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* 类型分布 */}
      <Card title="类型分布">
        <Row gutter={16}>
          {Object.entries(statistics.typeStatistics || {}).map(([type, count]) => {
            const config = getTypeConfig(type);
            return (
              <Col span={4} key={type}>
                <Statistic
                  title={<Tag color={config.color}>{type}</Tag>}
                  value={count}
                />
              </Col>
            );
          })}
        </Row>
      </Card>

      {/* 趋势图数据 */}
      {statistics.trendStatistics && statistics.trendStatistics.length > 0 && (
        <Card title="最近30天提交趋势" style={{ marginTop: 16 }}>
          <div style={{ height: 300, padding: '20px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={statistics.trendStatistics.map(item => ({
                  date: item._id,
                  count: item.count,
                  formattedDate: new Date(item._id).toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
                }))}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="formattedDate"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  label={{ value: '反馈数量', angle: -90, position: 'insideLeft' }}
                />
                <RechartsTooltip 
                  labelFormatter={(value) => `日期: ${value}`}
                  formatter={(value, name) => [value, '反馈数量']}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#1890ff" 
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#1890ff' }}
                  activeDot={{ r: 6, fill: '#1890ff' }}
                />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ textAlign: 'center', marginTop: 10, fontSize: '12px', color: '#666' }}>
              <Text type="secondary">
                总计 {statistics.trendStatistics.reduce((sum, item) => sum + item.count, 0)} 条反馈
              </Text>
            </div>
          </div>
        </Card>
      )}
    </div>
  );

  return (
    <div style={{ padding: '20px' }}>
      {/* 页面头部 */}
      <div style={{ marginBottom: '20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              <BugOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
              BUG反馈管理
            </Title>
          </Col>
        </Row>
      </div>

      {/* 主要内容 */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane
          tab={
            <span>
              <BugOutlined />
              反馈列表
              {(() => {
                const pendingAndProcessingCount = (statistics.overview?.pendingReports || 0) + (statistics.overview?.processingReports || 0);
                return pendingAndProcessingCount > 0 && <Badge count={pendingAndProcessingCount} style={{ marginLeft: 8 }} />;
              })()}
            </span>
          }
          key="list"
        >
          {renderReportList()}
        </TabPane>
        
        <TabPane
          tab={
            <span>
              <BarChartOutlined />
              统计信息
            </span>
          }
          key="statistics"
        >
          {renderStatistics()}
        </TabPane>
      </Tabs>

      {/* 详情弹窗 */}
      <Modal
        title="反馈详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="edit" type="primary" onClick={() => {
            setDetailModalVisible(false);
            handleEdit(selectedReport);
          }}>
            编辑
          </Button>,
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={900}
      >
        {selectedReport && (
          <div>
            <Row gutter={16}>
              <Col span={16}>
                <Title level={4}>{selectedReport.title}</Title>
              </Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <Space orientation="vertical" size="small">
                  <Tag color={getTypeConfig(selectedReport.bugType).color}>
                    {selectedReport.bugType}
                  </Tag>
                  <Tag color={getPriorityConfig(selectedReport.priority).color}>
                    {selectedReport.priority}
                  </Tag>
                  <Tag icon={getStatusConfig(selectedReport.status).icon} color={getStatusConfig(selectedReport.status).color}>
                    {selectedReport.status}
                  </Tag>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>提交者：</Text>
                <br />
                <Text>{selectedReport.submitter.username} ({selectedReport.submitter.email})</Text>
              </Col>
              <Col span={12}>
                <Text strong>处理人：</Text>
                <br />
                <Text>{selectedReport.assignedTo ? 
                  `${getDisplayName(selectedReport.assignedTo)} (${selectedReport.assignedTo.email})` : 
                  '未分配'
                }</Text>
              </Col>
            </Row>

            <Divider />

            <div style={{ marginBottom: 16 }}>
              <Text strong>问题描述：</Text>
              <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                {selectedReport.description}
              </Paragraph>
            </div>

            {selectedReport.processingNotes && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>处理备注：</Text>
                <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                  {selectedReport.processingNotes}
                </Paragraph>
              </div>
            )}

            {selectedReport.resolutionNotes && (
              <div style={{ marginBottom: 16 }}>
                <Text strong>解决方案：</Text>
                <Paragraph style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                  {selectedReport.resolutionNotes}
                </Paragraph>
              </div>
            )}

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Text strong>浏览器信息：</Text>
                <br />
                <Text type="secondary">{selectedReport.browserInfo || '-'}</Text>
              </Col>
              <Col span={12}>
                <Text strong>设备信息：</Text>
                <br />
                <Text type="secondary">{selectedReport.deviceInfo || '-'}</Text>
              </Col>
            </Row>

            <Divider />

            <Row gutter={16}>
              <Col span={8}>
                <Text strong>提交时间：</Text>
                <br />
                <Text type="secondary">{formatDate(selectedReport.submittedAt)}</Text>
              </Col>
              <Col span={8}>
                <Text strong>最后更新：</Text>
                <br />
                <Text type="secondary">{formatDate(selectedReport.lastUpdatedAt)}</Text>
              </Col>
              {selectedReport.resolvedAt && (
                <Col span={8}>
                  <Text strong>解决时间：</Text>
                  <br />
                  <Text type="secondary">{formatDate(selectedReport.resolvedAt)}</Text>
                </Col>
              )}
            </Row>
          </div>
        )}
      </Modal>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑反馈"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label="状态"
              >
                <Select placeholder="选择状态">
                  {statuses.map(status => (
                    <Option key={status.value} value={status.value}>
                      <Tag icon={status.icon} color={status.color}>{status.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="优先级"
              >
                <Select placeholder="选择优先级">
                  {priorities.map(priority => (
                    <Option key={priority.value} value={priority.value}>
                      <Tag color={priority.color}>{priority.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="assignedTo"
            label="分配处理人"
          >
            <Select placeholder="选择处理人" allowClear>
              {assignableUsers.map(user => (
                <Option key={user._id} value={user._id}>
                  {getDisplayName(user)} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="processingNotes"
            label="处理备注"
          >
            <TextArea
              rows={4}
              placeholder="添加处理备注..."
            />
          </Form.Item>

          <Form.Item
            name="resolutionNotes"
            label="解决方案"
          >
            <TextArea
              rows={4}
              placeholder="添加解决方案..."
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                更新
              </Button>
              <Button onClick={() => {
                setEditModalVisible(false);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量操作弹窗 */}
      <Modal
        title={`批量操作 (${selectedRowKeys.length} 项)`}
        open={batchModalVisible}
        onCancel={() => {
          setBatchModalVisible(false);
          batchForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchUpdate}
        >
          <Form.Item
            name="status"
            label="状态"
          >
            <Select placeholder="选择状态" allowClear>
              {statuses.map(status => (
                <Option key={status.value} value={status.value}>
                  <Tag icon={status.icon} color={status.color}>{status.label}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="assignedTo"
            label="分配处理人"
          >
            <Select placeholder="选择处理人" allowClear>
              {assignableUsers.map(user => (
                <Option key={user._id} value={user._id}>
                  {getDisplayName(user)} ({user.email})
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                批量更新
              </Button>
              <Button onClick={() => {
                setBatchModalVisible(false);
                batchForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminBugReportManagement; 